import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { gql } from '@apollo/client';
import { GetServerSidePropsContext } from 'next';
import {
  GetCommissionTierBySlugQuery,
  GetCommissionTierBySlugQueryVariables,
} from 'generated/graphql';
import { getClient } from 'db/contentAPI';
import {
  ArtistCollection,
  Collection,
  getImagesFromCollection,
} from 'logic/collections';
import ImageModal from '@components/ImageModal';
import { CollectionImage } from 'logic/images';
import { useRouter } from 'next/router';
import LoadingPage from '@components/LoadingPage';
import { routes } from '@utils/routes';

export default function CollectionGallery({
  data,
}: {
  data: Collection;
}): ReactElement {
  const router = useRouter();
  const [imgs, setImages] = useState<CollectionImage[]>([]);
  const currentTokenId = router.query.tokenId
    ? Number(router.query.tokenId)
    : 0;
  const currentIdx = imgs.findIndex((i) => i.tokenId === currentTokenId);

  const handleChangeSlide = useCallback(
    (nextIdx: number) => {
      router.replace(
        {
          query: {
            ...router.query,
            tokenId: imgs[nextIdx].tokenId.toString(),
          },
        },
        undefined,
        {
          shallow: true,
        }
      );
    },
    [router, imgs]
  );

  useEffect(() => {
    (async () => {
      const images = await getImagesFromCollection(data);
      setImages(images);
    })();
  }, [data]);

  useLayoutEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIdx = imgs[currentIdx + 1] ? currentIdx + 1 : 0;
        handleChangeSlide(nextIdx);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const nextIdx = imgs[currentIdx - 1] ? currentIdx - 1 : imgs.length - 1;
        handleChangeSlide(nextIdx);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentIdx, handleChangeSlide, imgs]);

  return imgs.length > 0 ? (
    <ImageModal
      collectionImages={imgs}
      displayIdx={currentIdx}
      setDisplayIdx={(nextIdx) => {
        handleChangeSlide(nextIdx);
      }}
      setShowModal={() => {
        if (history.state.idx === 0) {
          router.push(routes.collectionDetails(router.query.slug as string));
        } else {
          router.back();
        }
      }}
    />
  ) : (
    <LoadingPage />
  );
}

const queryImageGalleryBySlug = gql`
  query GetImageCollectionGallery($slug: String!, $preview: Boolean) {
    collectionCollection(where: { slug: $slug }, preview: $preview) {
      items {
        ...artistCollection
      }
    }
  }
  ${ArtistCollection}
`;

export async function getServerSideProps({
  res,
  preview,
  previewData,
  query: { slug },
}: GetServerSidePropsContext) {
  const { data, error } = await getClient(
    (previewData as { api: string })?.api
  ).query<GetCommissionTierBySlugQuery, GetCommissionTierBySlugQueryVariables>({
    query: queryImageGalleryBySlug,
    variables: {
      preview,
      slug: slug as string,
    },
  });

  if (!preview && !error) {
    res.setHeader(
      'Cache-Control',
      'public, s-maxage=60, stale-while-revalidate=59'
    );
  }

  return {
    props: {
      data: data?.collectionCollection?.items?.[0],
      preview: preview ?? false,
    },
  };
}
